/**
 * Connectivity Context for Kiosk Resilience
 *
 * Provides centralized connectivity state management:
 * - Tracks online/offline status
 * - Health poller with auto-recovery using native HTTP on Capacitor
 * - Integrates with circuit breaker in axiosClient
 * - Exposes state to OfflineOverlay and other components
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { CapacitorHttp } from '@capacitor/core';
import { setConnectivityCallbacks, resetCircuitBreaker, isCircuitOpen } from '../config/axiosClient.js';
import { getApiBaseUrl_dynamic, getIsCapacitor } from '../config/config.js';

// ─── Configuration ───────────────────────────────────────────────────────────
const HEALTH_POLL_INTERVAL_MS = 5000; // 5 seconds when offline
const HEALTH_POLL_INTERVAL_ONLINE_MS = 30000; // 30 seconds when online (less aggressive)
const HEALTH_ENDPOINT = '/health';
const HEALTH_TIMEOUT_MS = 5000;

// ─── Context ─────────────────────────────────────────────────────────────────
const ConnectivityContext = createContext({
  isOnline: true,
  isChecking: false,
  lastCheckTime: null,
  consecutiveFailures: 0,
  checkHealth: () => {},
});

/**
 * Connectivity Provider - wraps app and manages connectivity state
 */
export const ConnectivityProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);

  const healthPollIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Check if CapacitorHttp is available (native HTTP, avoids mixed content)
   */
  const isCapacitorHttpAvailable = () => {
    try {
      return getIsCapacitor() && typeof CapacitorHttp?.get === 'function';
    } catch {
      return false;
    }
  };

  /**
   * Perform health check using native HTTP on Capacitor to avoid mixed content
   */
  const checkHealth = useCallback(async () => {
    if (!isMountedRef.current) return;

    setIsChecking(true);

    try {
      const baseUrl = getApiBaseUrl_dynamic() || '';
      const healthUrl = `${baseUrl}${HEALTH_ENDPOINT}`;
      let isOk = false;

      // Use native HTTP on Capacitor to avoid mixed-content blocking
      if (isCapacitorHttpAvailable()) {
        try {
          const response = await Promise.race([
            CapacitorHttp.get({
              url: healthUrl,
              connectTimeout: HEALTH_TIMEOUT_MS,
              readTimeout: HEALTH_TIMEOUT_MS,
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), HEALTH_TIMEOUT_MS + 500)
            ),
          ]);
          isOk = response.status >= 200 && response.status < 300;
        } catch (nativeErr) {
          console.log('Health check failed (native):', nativeErr.message);
          isOk = false;
        }
      } else {
        // Web fallback
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);

        try {
          const response = await fetch(healthUrl, {
            method: 'GET',
            signal: controller.signal,
            cache: 'no-store',
          });
          clearTimeout(timeoutId);
          isOk = response.ok;
        } catch (fetchErr) {
          clearTimeout(timeoutId);
          isOk = false;
        }
      }

      if (!isMountedRef.current) return;

      if (isOk) {
        // Online!
        if (!isOnline) {
          console.log('🟢 Connectivity restored');
        }
        setIsOnline(true);
        setConsecutiveFailures(0);
        resetCircuitBreaker();
      } else {
        throw new Error('Health check failed');
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      const newFailures = consecutiveFailures + 1;
      setConsecutiveFailures(newFailures);

      // Only go offline after 2 consecutive failures (avoid false positives)
      if (newFailures >= 2) {
        if (isOnline) {
          console.log('🔴 Connectivity lost');
        }
        setIsOnline(false);
      }
    } finally {
      if (isMountedRef.current) {
        setIsChecking(false);
        setLastCheckTime(Date.now());
      }
    }
  }, [isOnline, consecutiveFailures]);

  /**
   * Handle going offline (called by circuit breaker)
   */
  const handleOffline = useCallback(() => {
    if (isMountedRef.current && isOnline) {
      console.log('🔴 Circuit breaker triggered offline state');
      setIsOnline(false);
    }
  }, [isOnline]);

  /**
   * Handle going online (called by circuit breaker reset)
   */
  const handleOnline = useCallback(() => {
    if (isMountedRef.current && !isOnline) {
      console.log('🟢 Circuit breaker reset - online');
      setIsOnline(true);
      setConsecutiveFailures(0);
    }
  }, [isOnline]);

  // Register callbacks with axiosClient
  useEffect(() => {
    setConnectivityCallbacks(handleOffline, handleOnline);
  }, [handleOffline, handleOnline]);

  // Start health poller
  useEffect(() => {
    isMountedRef.current = true;

    // Initial check after a short delay
    const initialTimeout = setTimeout(() => {
      if (isMountedRef.current) {
        checkHealth();
      }
    }, 2000);

    // Set up polling interval
    const startPolling = () => {
      if (healthPollIntervalRef.current) {
        clearInterval(healthPollIntervalRef.current);
      }

      // Poll more frequently when offline
      const interval = isOnline ? HEALTH_POLL_INTERVAL_ONLINE_MS : HEALTH_POLL_INTERVAL_MS;

      healthPollIntervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          checkHealth();
        }
      }, interval);
    };

    startPolling();

    // Restart polling when online state changes (to adjust interval)
    return () => {
      isMountedRef.current = false;
      clearTimeout(initialTimeout);
      if (healthPollIntervalRef.current) {
        clearInterval(healthPollIntervalRef.current);
      }
    };
  }, [checkHealth, isOnline]);

  // Listen for browser online/offline events as additional signal
  useEffect(() => {
    const handleBrowserOnline = () => {
      console.log('🌐 Browser reports online - checking health');
      checkHealth();
    };

    const handleBrowserOffline = () => {
      console.log('🌐 Browser reports offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleBrowserOnline);
    window.addEventListener('offline', handleBrowserOffline);

    return () => {
      window.removeEventListener('online', handleBrowserOnline);
      window.removeEventListener('offline', handleBrowserOffline);
    };
  }, [checkHealth]);

  const value = {
    isOnline,
    isChecking,
    lastCheckTime,
    consecutiveFailures,
    checkHealth,
    isCircuitOpen: isCircuitOpen(),
  };

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  );
};

/**
 * Hook to access connectivity state
 */
export const useConnectivity = () => {
  const context = useContext(ConnectivityContext);
  if (!context) {
    throw new Error('useConnectivity must be used within a ConnectivityProvider');
  }
  return context;
};

export default ConnectivityContext;
